<script>
   /*************************************************************/
   /* AppContainer for test set                                 */
   /* -------------------------                                 */
   /* app container for testing a PLS1-DA model                 */
   /*************************************************************/

   import { Button, Container } from 'svelte-controls-basic';
   import { plsmodel_get, plsres_get } from '@mdatools-apps/wasm/pls.js';
   import { app_residuals } from '@mdatools-apps/shared/utils';
   import { downloadResults, downloadModel, predict, getIndices, getStatMatrices } from '../utils';

   import Relabeled from '@mdatools-apps/shared/widgets/Relabeled.svelte';
   import Outliers from '@mdatools-apps/shared/widgets/Outliers.svelte';
   import Contributions from '@mdatools-apps/shared/components/Contributions.svelte';

   import DataLoader from '../shared/DataLoader.svelte';
   import ModelLoader from '../shared/ModelLoader.svelte';
   import ResultTable from '../shared/ResultTable.svelte';
   import PLSDASettings from '../shared/PLSDASettings.svelte';

   import AppPlots from './AppPlots.svelte';

   // WASM promise and session object from parent app
   let {WASM, backupSession, session = $bindable()} = $props();

   // get part of the session for this tab
   const s = $derived(session['test']);

   // properties for showing contributions
   let selected = $state(-1);
   let contributionIsShown = $state(false);


   // callback function to set object with a give position in plot series
   // as outlier
   function markOutlier(pos) {

      // check that position is valid
      if (pos < 0 || pos > s.testres.pls.nrows - 1) return;

      // find sample ID corresponding to the position
      // this is needed if some of the objects already
      // removed from results as outliers
      const id = s.testres.pls.objindices[pos];

      // add the object to outliers array and reset the model
      if (id >= 0 && !s.outliers.includes(id)) {
         s.outliers.push(id);
         s.testres = {pls: null, da: null};
         s.ind = null;
      }
   }

   // callback to reset outliers
   function resetOutliers() {
      s.outliers = [];
      s.testres = {pls: null, da: null};
      s.ind = null;
   }

   // callback to undo outliers
   function undoOutliers() {
      s.outliers.pop();
      s.testres = {pls: null, da: null};
      s.ind = null;
   }

   // mark object with index "pos" as relabeled
   function markRelabeled(pos) {

      // check that position is valid
      if (pos < 0 || pos > s.testres.pls.nrows - 1) return;

      // find sample ID corresponding to the position
      // this is needed if some of the objects already
      // removed from results as outliers
      const id = s.testres.pls.objindices[pos];

      if (!s.relabeled) {
         s['relabeled'] = [];
      }

      // add the object to outliers array and reset the model
      if (id >= 0 && !s.relabeled.includes(id)) {
         s.relabeled.push(id);
         s.testres = {pls: null, da: null};
         s.ind = null;
      }
   }

   // reset list of relabeled objects, outcomes and the model
   function resetRelabeled() {
      s.relabeled = [];
      s.testres = {pls: null, da: null};
      s.ind = null;
   }

   // remove last added relabeled object from the list
   function undoRelabeled() {
      s.relabeled.pop();
      s.testres = {pls: null, da: null};
      s.ind = null;
   }

   // show modal dialog with contributions for selected object
   function showContributions(pos) {
      if (pos < 0) return;
      selected = pos;
      contributionIsShown = true;
   }

   // hide modal dialog with contributions
   function hideContributions() {
      contributionIsShown = false;
      selected = -1;
   }

   // callback to set model parameters from heatmap plot
   function setModelParams(p) {
      s.model.da.ncomp = p[0];
      if (s.model.da.rule === 'hard') {
         s.model.da.threshold = p[1];
      } else {
         s.model.da.alpha = p[1];
      }
   }


   // effect to track changes in dataset and models
   $effect(() => {

      // if test set is set null - reset results and all related parameters.
      // 'relabeled' holds row numbers of the dataset which has just gone, so it has to go with
      // it - the outlier list beside it has always been cleared here. Left behind, its indices
      // point into the NEXT dataset, and 'relabelObjects' cannot save us: an index past the end
      // of that dataset is refused, which costs the user an error blaming a list they never built
      // for this file, and an index still inside it is accepted and flips a DIFFERENT object than
      // the one they chose. The second case is why the list has to be cleared here rather than
      // validated later - no guard can tell that index from an honest one
      if (s.testset === null) {
         s.outliers = [];
         s.relabeled = [];
         s.testres = {pls: null, da: null};
         s.ind = null;
      }

      // if model object is set to null - reset dependent objects
      if (s.model === null) {
         s.testres = {pls: null, da: null};
         s.ind = null;
         s.testset = null;
      }
   });

   $effect(() => {
      if (s.testres && s.testres.pls) {
         s.ind = getIndices(s.testres.pls.Yref.getcolref(1), s.testset.classNames);
      }
   });


   // the heatmap's figures of merit, derived and not stored (see the train tab)
   const statmatTest = $derived(s.testres.pls && s.model.da ?
      getStatMatrices(s.testres.pls, s.model.da.params, s.ind, s.model.da.classNames) : null);


   // recalculate DA results for calibration set when DA parameters have changed
   $effect(() => {
      if (s.testres.pls && s.model.da) {
         s.testres.da = predict(s.testres.pls, s.model.da, s.ind)
      }
   });


   // reactive effect for backing up the session. the session is read by 'backupSession' when the
   // write is due, not here, and the first run is skipped: it only ever coincides with a session
   // the app was given as a whole, which is already on disk, is written by 'MainApp' itself, or is
   // a fresh one that need not be stored. the values after the function are the dependencies (see
   // 'backupSession'). 'model.da.ncomp' is the LV slider, a 'Range' which writes once per step
   // crossing while the pointer is down; 'predict' does not read it, so under the hard rule a
   // step changed nothing else listed here and the number was stored with the next change of a
   // field that is listed
   let mounted = false;
   $effect(() => {
      const deps = [s.testset, s.model, s.testres.da, s.model?.da?.ncomp];
      if (mounted) backupSession(() => session, ...deps);
      mounted = true;
   });


   const indSelected = $derived.by(() => {
      if (selected < 0 || !s.testres.pls || !s.testres.pls.Yref) return null;
      const y = s.testres.pls.Yref.v[selected];
      const ci = y > 0 ? 0 : 1;
      const cn = s.model.da.classNames[ci];
      return s.ind[cn].map(v => v + 1);
   });
</script>

<div class="app-container">
   {#if contributionIsShown}
   <Contributions
      model_getter={plsmodel_get}
      res_getter={plsres_get}
      model={s.model.pls}
      res={{test: s.testres.pls}}
      residuals={() => WASM().then((M) =>
         app_residuals(M, s.model.pls, s.testset, s.testres.pls, true))}
      ncomp={s.model.da.ncomp}
      {selected}
      ind={indSelected}
      closeModal={hideContributions}
   />
   {/if}

   <!-- left column with main app controls -->
   <div class="app-controls">
      <div class="model-loader">
         <ModelLoader bind:model={s.model} />
      </div>

      {#if s.model && s.model.pls}
      <div class="data-loader">
         <DataLoader
            {WASM}
            bind:dataset={s.testset}
            bind:resobj={s.testres.pls}
            outliers={s.outliers}
            relabeled={s.relabeled}
            model={s.model.pls}
            classNames={s.model.da ? s.model.da.classNames : null}
            classMandatory={true}
         />
      </div>

      {#if s.outliers && s.outliers.length > 0 }
      <div class="outliers-pane">
         <Outliers outliers={s.outliers} dataset={s.testset} {resetOutliers} {undoOutliers} />
      </div>
      {/if}

      {#if s.relabeled && s.relabeled.length > 0 }
      <div class="outliers-pane">
         <Relabeled relabeled={s.relabeled} dataset={s.testset} {resetRelabeled} {undoRelabeled} />
      </div>
      {/if}


      {#if s.model.pls && s.testres.pls}
      <div class="ncomp-selector">
         <PLSDASettings
            plsmodel={s.model.pls}
            plsres={s.testres.pls}
            ind={s.ind}
            classNames={s.model.da.classNames}
            bind:damodel={s.model.da}
         />
         <Container label='Download'>
            <div class="buttons">
            <Button onclick={() => downloadResults(s.testres, s.testset, "test", s.model.da.ncomp)} text="Res (test)"/>
            <Button onclick={() => downloadModel(s.model, s.model.pls.prep)} text="Model" />
            </div>
         </Container>
      </div>
      {#if s.testres.da}
      <div class="result-stats">
         <ResultTable res={{test: s.testres.da}} model={s.model.da} />
      </div>
      {/if}
      {/if}
   {/if}

   </div>

   <!-- right column with help text or with plots and plot controls -->
   <div class="app-content">
      {#if !s.testres.da}
         <div class="help-text">
         {#if !s.model }
            <p>
               In this tab you can test a PLS1-DA classification model developed in <strong>Train</strong> tab by
               applying it to a test set, which contains both predictors and responses.
            </p>
            <p><strong>Step 1.</strong></p>
            <p>Load the previously developed PLS1-DA model from a JSON file.</p>
         {:else if !s.testset}
            <p><strong>Step 2.</strong></p>
            <p>Load your test set from a CSV file. The file structure should be the same as the training/calibration set.
               The column with class labels is mandatory and it should contain the same classes your model was trained for.
               Here is an example how data structure may look like (note, that row labels
               and header are optional):
            </p>
            <div>
               <table>
                  <thead>
                  <tr>
                     <td>Name</td>
                     <td>Class</td>
                     <td>x1</td>
                     <td>x2</td>
                     <td>x3</td>
                  </tr>
                  </thead>
                  <tbody>
                  <tr>
                     <td>O1</td>
                     <td>Red</td>
                     <td>3.8</td>
                     <td>1.3</td>
                     <td>13.6</td>
                  </tr>
                  <tr>
                     <td>O2</td>
                     <td>Red</td>
                     <td>11.1</td>
                     <td>12.3</td>
                     <td>3.3</td>
                  </tr>
                  <tr>
                     <td>O3</td>
                     <td>Blue</td>
                     <td>1.2</td>
                     <td>2.3</td>
                     <td>4.2</td>
                  </tr>
                  </tbody>
               </table>
            </div>
            <p></p>

         {/if}
         </div>
      {:else}

         <!-- keyed on the model and not only guarded by it: a session file whose date equals the
              open session's, which is the same file loaded a second time, leaves the tabs mounted
              while every field is swapped for a new object at once, and the plot container
              captures its controls when it mounts -->
         {#key s.testres.pls && s.model }
         {#if s.testres && s.testres.pls && s.testres.da && s.model && s.model.pls && s.model.da}
         <AppPlots
            {markOutlier}  {showContributions} {setModelParams}
            {markRelabeled}
            data={{'test': s.testset}}
            res={{'test': s.testres}}
            model={s.model}
            statMat={{test: statmatTest}}
         />
         {/if}
         {/key}
      {/if}
   </div>
</div>

<style>

   .app-container {
      font-family: Helvetica, Arial, sans-serif;
      font-size: 1rem;
      color: #404040;
      box-sizing: border-box;
      margin: 0 auto;
      padding: 0.35em 0.25em;
      width: 100%;
      height: 100%;
      display: grid;
      grid-template-areas: "controls content";
      grid-template-rows: 100%;
      grid-template-columns: min(310px, 30%) 1fr;
   }

   .app-controls {
      box-sizing: border-box;
      padding: 0.25em 0.5em;
   }

   .app-content {
      box-sizing: border-box;
      padding: 0.25em 0.25em;
      height: 100%;
   }

   .app-controls > div {
      border-radius: 0.25rem;
   }

   .app-controls > .result-stats {
      background: #e8e8e8;
   }

   .help-text {
      margin-top: 0.25rem;
   }

   .help-text > p, .help-text > div {
      background: #fafafa;
      padding: 0em 1.5em 0.5em 1.5em;
      color: #606060;
      line-height: 1.5em;
      margin: 0 1em 0 0;
   }

   .help-text table {
      border-collapse: collapse;
   }

   .help-text table td{
      padding: 0.25em 0.5em;
      background: #e0f0fa;
      text-align: right;
      border: 1px solid white;
   }

   .help-text table thead tr td{
      font-weight: bold;
      background: #f8f4e0;
   }

   .help-text table td:first-child {
      font-weight: bold;
      background: #f8f4e0;
      padding-right: 1em;
   }

   .help-text table tbody td:nth-child(2) {
      background: #e0e8f0;
      padding-right: 1em;
   }

   .help-text > p:first-child {
      border-top-left-radius: 0.25rem;
      border-top-right-radius: 0.25rem;
      padding-top: 1.5em;
   }

   .help-text > p:last-child {
      border-bottom-left-radius: 0.25rem;
      border-bottom-right-radius: 0.25rem;
      padding-bottom: 1.5em;
   }

   .result-stats {
      padding: 0.5rem 1rem;
      margin-top: 0.25rem;
      font-size: 0.85em;
   }


   @media(min-width:1100px) {
      .app-container {
         grid-template-columns: min(330px, 30%) 1fr;
      }
   }

   @media(min-width:1300px) {
      .app-container {
         grid-template-columns: min(340px, 30%) 1fr;
      }
   }

</style>